import { BottomSheet } from '@kcd/design-system';
import { Suspense, useEffect, useState } from 'react';
import cx from 'clsx';
import { trackEvent } from '@kcd/event-tracker';

import { useProductOptions } from '~/api/domains/products';
import { ChevronDownOutlineIcon24x24 } from '~/assets/icons';
import useProductBottomSheetStore from '~/store/global/useProductBottomSheetStore';
import useProductOptionStore from '~/store/global/useProductOptionStore';

import { Select } from '~/components/Select';

import { OrderButtonContainer } from './OrderButtonContainer';
import { MembershipNudge } from './MembershipNudge';
import { ProductOptionItem } from './ProductOptionItem';
import { SelectedProductOption } from './SelectedProductOption';
import { TotalAmountContainer } from './TotalAmountContainer';
import type { ProductOption } from './types';

type ProductOptionListContainerProps = {
  offerId: number;
  opened: boolean;
};

function ProductOptionListContainer({
  offerId,
  opened,
}: ProductOptionListContainerProps) {
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ProductOption[]>([]);
  const { setOfferId } = useProductOptionStore();
  const { data } = useProductOptions(offerId);

  const onSelectOption = (selectedId: number) => {
    const target = options.find(({ skuId }) => skuId === selectedId);
    const isChecked = !!selectedOptions.find(
      ({ skuId }) => skuId === selectedId,
    );

    if (target && !isChecked) {
      setSelectedOptions([
        ...selectedOptions,
        {
          ...target,
          productName: data?.productName || '',
          count: target.calcMinimumOrderQuantity || 1,
        },
      ]);
    }
  };

  const onDeleteSelectedOption = (skuId: number) => {
    const newList = selectedOptions.filter((option) => option.skuId !== skuId);
    setSelectedOptions(newList);
  };

  const onChangeOptionCount = (skuId: number, count: number) => {
    const newList = selectedOptions.map((option) => {
      if (skuId === option.skuId) {
        return { ...option, count };
      }
      return option;
    });
    setSelectedOptions(newList);
  };

  useEffect(() => {
    if (!data || !data.options?.length) return;
    const productOptions = data.options;

    const newProductOptions = productOptions.map((option) => ({
      ...option,
      offerId,
      name: option.name || data.productName,
      count: option.calcMinimumOrderQuantity || 1,
    }));

    setOptions(newProductOptions as ProductOption[]);

    // 단일 상품 옵션은 첫번째 옵션을 선택한 상태
    // 다중 상품 옵션인 경우, 기본 선택값이 없음
    if (productOptions.length === 1) {
      const optionsOnlyFirst = [
        {
          ...productOptions[0],
          offerId,
          productName: data.productName!,
          name: productOptions[0].name || data.productName,
          count: productOptions[0].calcMinimumOrderQuantity || 1,
        },
      ];

      setSelectedOptions(optionsOnlyFirst as ProductOption[]);
    }
  }, [data, offerId]);

  useEffect(() => {
    if (!opened) {
      setOfferId(0);
      setOptions([]);
      setSelectedOptions([]);
    } else {
      trackEvent('viewed bottomSheet in 마켓', {
        type: '주문퍼널 추천상품 옵션',
        productName: data?.productName,
        offerId,
      });
    }
  }, [data?.productName, offerId, opened, setOfferId]);

  const isSingle = options.length === 1;

  if (options.length === 0) {
    return <div className="h-[calc(40vh+98px)] min-h-[30vh]" />;
  }

  return (
    <>
      <div
        className={cx([
          'h-[35vh] min-h-[30vh] overflow-y-auto mb-[10px]',
          !opened && 'hidden',
        ])}
      >
        {!isSingle && (
          <Select<number>
            placeholder="상품 옵션을 선택해주세요."
            onChange={onSelectOption}
            defaultOpened
            outsideClickClosed={false}
          >
            {options.map((option) => {
              const disabled = option.status !== 'DISPLAYING';
              return (
                <Select.Option
                  disabled={disabled}
                  value={option.skuId}
                  key={option.skuId}
                >
                  <ProductOptionItem option={option} disabled={disabled} />
                </Select.Option>
              );
            })}
          </Select>
        )}
        {selectedOptions.map((option) => (
          <SelectedProductOption
            option={option}
            key={option.skuId}
            onChange={(value) => onChangeOptionCount(option.skuId!, value)}
            onDelete={
              isSingle ? undefined : () => onDeleteSelectedOption(option.skuId!)
            }
          />
        ))}
      </div>

      <TotalAmountContainer
        offerId={offerId}
        selectedProductOptions={selectedOptions}
      />
      <OrderButtonContainer
        selectedOptions={selectedOptions}
        mallName={data?.mallName}
      />
    </>
  );
}

/**
 * 상품 옵션 선택 바텀시트 (App Router 버전).
 *
 * Pages 의 `~/components/CommonProductBottomSheet` 를 이관한 것으로,
 * zustand store(useProductBottomSheetStore/useProductOptionStore) 로 열림/대상 offerId 를 받는다.
 * AppRootProvider 에서 한 번 mount 되어 App Router 트리 전역에서 동작한다.
 *
 * MVP 범위: 멤버십 넛지·cross-sell 바텀시트·snackbar 는 후속 이관(가드 처리).
 */
export function ProductOptionBottomSheet() {
  const { opened, closeBottomSheet } = useProductBottomSheetStore();
  const { offerId } = useProductOptionStore();

  const onClose = () => {
    closeBottomSheet();
  };

  return (
    <BottomSheet
      hideDismiss
      opened={opened}
      onClose={onClose}
      maxHeight={75}
      className="!z-20"
    >
      <div
        role="presentation"
        className="absolute inset-x-0 top-6 flex h-20 items-center justify-center"
        onClick={onClose}
      >
        <ChevronDownOutlineIcon24x24 className="size-24" />
      </div>
      {Boolean(offerId) && (
        <>
          <Suspense fallback={null}>
            <MembershipNudge />
          </Suspense>
          <ProductOptionListContainer offerId={offerId} opened={opened} />
        </>
      )}
    </BottomSheet>
  );
}
